import { useHotkeys } from "@mantine/hooks";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import {
  incrementTaggingFlowStep,
  setTaggingFlowStep,
} from "../../../../store/slices/hotkeys";
import { selectIsSoftball } from "../../../../store/slices/socket/selectors";
import type { RootState } from "../../../../store/store";
import { ContactTypeStep } from "./ContactTypeStep";
import { OutsRecordedStep } from "./OutsRecordedStep";
import { PitchResultStep } from "./PitchResultStep";
import { PitchTypeStep } from "./PitchTypeStep";
import { PlayResultStep } from "./PlayResultStep";
import { PrePitchStep } from "./PrePitchStep";
import { RunsScoredStep } from "./RunsScoredStep";
import { SoftballPitchTypeStep } from "./SoftballPitchTypeStep";

/**
 * TaggingSteps - Vertical tab interface for pitch/play tagging workflow
 *
 * 7-step tagging process:
 * 1. Pre Pitch Stance (Windup/Stretch)
 * 2. Pitch Type (Fastball, Curveball, etc.)
 * 3. Pitch Result (Strike, Ball, etc.)
 * 4. Play Result (Single, Out, etc.)
 * 5. Contact Type (Ground ball, Fly ball, etc.)
 * 6. Outs Recorded (0-3)
 * 7. Runs Scored (0-4)
 *
 * Features:
 * - Tab key advances to next step
 * - Orange highlight when step is completed
 * - Keyboard shortcuts for each option
 */
export const TaggingSteps = () => {
  const dispatch = useDispatch();

  const taggingFlowStep = useSelector(
    (state: RootState) => state.hotkeys.taggingFlowStep,
  );
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const isSoftball = useSelector(selectIsSoftball);

  const onTabChange = (stepIndex: number) => {
    dispatch(setTaggingFlowStep(stepIndex));
  };

  // Tab key moves to next tagging step
  useHotkeys([
    [
      "Tab",
      () => {
        if (taggingFlowStep < 6) {
          dispatch(incrementTaggingFlowStep());
        } else if (taggingFlowStep === 6) {
          dispatch(setTaggingFlowStep(0));
        }
      },
    ],
  ]);

  const steps = [
    {
      label: "Pre Pitch",
      completed: !!tagData?.prePitchStance,
      component: <PrePitchStep />,
    },
    {
      label: "Pitch Type",
      completed: !!tagData?.pitchType,
      component: isSoftball ? <SoftballPitchTypeStep /> : <PitchTypeStep />,
    },
    {
      label: "Pitch Result",
      completed: !!tagData?.pitchResult,
      component: <PitchResultStep />,
    },
    {
      label: "Play Result",
      completed: !!tagData?.playResult,
      component: <PlayResultStep />,
    },
    {
      label: "Contact Type",
      completed: !!tagData?.contactType,
      component: <ContactTypeStep />,
    },
    {
      label: "Outs Recorded",
      completed:
        tagData?.outsRecorded !== undefined &&
        [0, 1, 2, 3].includes(tagData.outsRecorded),
      component: <OutsRecordedStep />,
    },
    {
      label: "Runs Scored",
      completed:
        tagData?.runsScored !== undefined &&
        [0, 1, 2, 3, 4].includes(tagData.runsScored),
      component: <RunsScoredStep />,
    },
  ];

  return (
    <div className="flex h-96 w-full max-w-2xl rounded bg-white shadow-md">
      {/* Vertical Tab List */}
      <div className="flex w-36 flex-col border-r border-gray-200">
        {steps.map((step, index) => (
          <button
            key={step.label}
            onClick={() => onTabChange(index)}
            className={clsx(
              // Size
              "h-10",
              // Spacing
              "px-4",
              // Text
              "text-left text-sm font-blue",
              // Effects
              "outline-none transition-colors",
              // States
              taggingFlowStep === index
                ? "border-r-2 border-blue bg-[hsla(218,100%,52%,0.05)] text-blue shadow-sm"
                : "text-text-light hover:bg-gray-50",
              step.completed && taggingFlowStep !== index && "text-primary",
            )}
          >
            {step.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex flex-1 items-center justify-center p-4">
        {steps[taggingFlowStep]?.component}
      </div>
    </div>
  );
};
